import { Button, Flex } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { DateRangeFilter } from "../../../../components/Filters/DateRangeFilter.tsx"
import { ResolvedSelect } from "../../../../components/Select/ResolvedSelect.tsx"
import { LeadFilterField } from "../../../Leads/LeadsActivePage/components/leads-list-table/LeadFilterField.tsx"
import { LEAD_ACTIVITY_CATEGORIES, NO_FILTERS } from "./lead-activity.ts"
import type {
  LeadActivityFilters as Filters,
  LeadActivityUser
} from "./lead-activity.types.ts"

interface LeadActivityFiltersProps {
  filters: Filters
  users: LeadActivityUser[]
  onChange: (patch: Partial<Filters>) => void
}

export function LeadActivityFilters({
  filters,
  users,
  onChange
}: LeadActivityFiltersProps) {
  const { t } = useTranslation()

  const typeOptions = [
    { value: "all", label: t("leadWorkspace.activity.filters.allTypes") },
    ...LEAD_ACTIVITY_CATEGORIES.map((category) => ({
      value: category,
      label: t(`leadWorkspace.activity.categories.${category}`)
    }))
  ]

  const isFiltered =
    filters.category !== "all" ||
    !!filters.after ||
    !!filters.before ||
    !!filters.user

  return (
    <Flex
      wrap
      gap={"small"}
      align={"end"}
      data-testid={"lead-activity-filters"}
    >
      <LeadFilterField
        label={t("leadWorkspace.activity.filters.type")}
        className={"w-48 max-md:grow"}
      >
        <ResolvedSelect
          aria-label={t("leadWorkspace.activity.filters.type")}
          value={filters.category}
          options={typeOptions}
          onChange={(category) => onChange({ category })}
        />
      </LeadFilterField>

      <LeadFilterField
        label={t("leadWorkspace.activity.filters.dateRange")}
        className={"w-60 max-md:grow"}
      >
        <DateRangeFilter<Filters>
          after={filters.after}
          before={filters.before}
          afterKey={"after"}
          beforeKey={"before"}
          className={"w-full"}
          placeholder={t("leadWorkspace.activity.filters.dateRange")}
          onChange={onChange}
        />
      </LeadFilterField>

      <LeadFilterField
        label={t("leadWorkspace.activity.filters.user")}
        className={"w-52 max-md:grow"}
      >
        <ResolvedSelect
          allowClear
          showSearch
          optionFilterProp={"label"}
          aria-label={t("leadWorkspace.activity.filters.user")}
          placeholder={t("leadWorkspace.activity.filters.allUsers")}
          value={filters.user}
          options={users}
          onChange={(user) => onChange({ user: user ?? null })}
        />
      </LeadFilterField>

      <LeadFilterField label={" "}>
        <Button
          type={"link"}
          disabled={!isFiltered}
          onClick={() => onChange(NO_FILTERS)}
        >
          {t("leadWorkspace.activity.filters.clearAll")}
        </Button>
      </LeadFilterField>
    </Flex>
  )
}
